import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Button, Card, Col, Row } from "react-bootstrap";

import CsLineIcons from "../../../cs-line-icons";
import { send as pageInfo } from "../../../actions/page";
import { withNavigation } from "../../../utils/wrapper";
import AccountingImportButton, { AccountingImportWorkflow } from "./import";

const sections = [
    {
        title: "Work",
        items: [
            {
                title: "Ledger",
                detail: "Entries and account activity",
                icon: "dollar",
                to: "/dashboard/accounting/ledger"
            },
            {
                title: "Check Register",
                detail: "Checks and disbursements",
                icon: "print",
                to: "/dashboard/accounting/checks/register"
            },
            {
                title: "Reconciliations",
                detail: "Bank reconciliation work",
                icon: "check-square",
                to: "/dashboard/accounting/reconciliations",
                actions: [
                    { label: "New", icon: "plus", to: "/dashboard/accounting/reconciliations/add" }
                ]
            }
        ]
    },
    {
        title: "Accounts",
        items: [
            {
                title: "Chart of Accounts",
                detail: "Accounting account setup",
                icon: "money",
                to: "/dashboard/accounting/accounts",
                actions: [
                    { label: "New", icon: "plus", to: "/dashboard/accounting/accounts/add" }
                ]
            },
            {
                title: "Financial Accounts",
                detail: "Operating and trust accounts",
                icon: "money",
                to: "/dashboard/accounting/financial-accounts",
                actions: [
                    { label: "New", icon: "plus", to: "/dashboard/accounting/financial-accounts/add" }
                ]
            },
            {
                title: "Accounting Settings",
                detail: "Firm accounting defaults",
                icon: "gear",
                to: "/dashboard/settings/accounting"
            }
        ]
    },
    {
        title: "Payroll",
        items: [
            {
                title: "Payroll",
                detail: "Pay checks and earnings statements",
                icon: "money",
                to: "/dashboard/accounting/payroll"
            },
            {
                title: "Pay Periods",
                detail: "Period definitions",
                icon: "tag",
                to: "/dashboard/accounting/payperiods",
                actions: [
                    { label: "New", icon: "plus", to: "/dashboard/accounting/payperiods/add" }
                ]
            },
            {
                title: "Pay Schedules",
                detail: "Recurring payroll schedules",
                icon: "calendar",
                to: "/dashboard/accounting/payschedules",
                actions: [
                    { label: "New", icon: "plus", to: "/dashboard/accounting/payschedules/add" }
                ]
            }
        ]
    }
];

class AccountingDashboard extends React.Component {
    state = {
        importing: false
    };

    componentDidMount = () => {
        this.props.pageInfo({ title: "Accounting", breadcrumb: "Accounting" });
    };

    startImport = () => {
        this.setState({ importing: true });
        this.props.pageInfo({ title: "Import Time Entries", breadcrumb: "Import Time Entries" });
    };

    closeImport = () => {
        this.setState({ importing: false });
        this.props.pageInfo({ title: "Accounting", breadcrumb: "Accounting" });
    };

    open = (path) => {
        if (!path) return;
        this.props.navigate(path);
    };

    renderAction = (action) => (
        <Button
            key={`${action.label}-${action.to}`}
            type="button"
            size="sm"
            variant="outline-secondary"
            onClick={() => this.open(action.to)}
        >
            <CsLineIcons icon={action.icon} size="14" className="me-1" />
            {action.label}
        </Button>
    );

    renderItem = (item) => (
        <Col sm="6" xl="4" key={item.to}>
            <Card className="h-100">
                <Card.Body className="d-flex flex-column">
                    <div className="d-flex align-items-start gap-3">
                        <div
                            className="d-flex align-items-center justify-content-center bg-light border"
                            style={{ width: 40, height: 40, borderRadius: 6, flex: "0 0 40px" }}
                        >
                            <CsLineIcons icon={item.icon} size="21" />
                        </div>
                        <div className="w-100">
                            <button
                                type="button"
                                className="btn btn-link p-0 fw-bold text-start"
                                onClick={() => this.open(item.to)}
                            >
                                {item.title}
                            </button>
                            <div className="small text-muted">{item.detail}</div>
                        </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-4">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            onClick={() => this.open(item.to)}
                        >
                            <CsLineIcons icon="arrow-right" size="14" className="me-1" />
                            Open
                        </Button>
                        {(item.actions || []).map(this.renderAction)}
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );

    renderSection = (section) => (
        <Col xs="12" key={section.title}>
            <h2 className="small-title">{section.title}</h2>
            <Row className="g-3">
                {section.items.map(this.renderItem)}
            </Row>
        </Col>
    );

    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    <AccountingImportButton onStart={this.startImport} disabled={this.state.importing} />
                </Col>
                {this.state.importing ? (
                    <AccountingImportWorkflow show={this.state.importing} onHide={this.closeImport} />
                ) : (
                <Col xs="12">
                    <Row className="g-4">
                        {sections.map(this.renderSection)}
                    </Row>
                </Col>
                )}
            </>
        );
    }
}

AccountingDashboard.propTypes = {
    navigate: PropTypes.func.isRequired,
    pageInfo: PropTypes.func.isRequired
};

export default withNavigation(connect(null, { pageInfo })(AccountingDashboard));
